import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Wallet, Search, RefreshCw, Calendar, CreditCard, Building2, Printer, Download, X, Sparkles, XCircle, ChevronLeft, ChevronRight, Ban } from 'lucide-react';
import { useDataCache } from '@/hooks/useDataCache';
import { useToast } from '@/components/ui/use-toast';
import { formatLocalDate } from '@/lib/utils';

interface Empresa {
  id: number;
  profileName: string;
  legalName: string;
  rfc: string;
  isDefault: boolean;
}

interface FolioSerieOption {
  id: number;
  serie: string;
  invoicingSettingsId: number;
  isActive: boolean;
}

interface Payment {
  id: number;
  invoiceId: number;
  invoiceNumber: string;
  customerName: string;
  customerNui: string;
  amount: string;
  paymentMethod: string;
  paymentDate: string;
  reference: string | null;
  bankAccountName: string;
  notes: string | null;
  promotionId: number | null;
  promotionName: string | null;
  promotionDiscountAmount: string | null;
  createdAt: string;
  folioSeriesId?: number | null;
  folio?: number | null;
  status?: string;
  serieName?: string | null;
  invoicingSettingsId?: number | null;
  createdByUserId?: number | null;
  createdByName?: string | null;
}

interface InvoiceDetail {
  invoiceNumber: string;
  date: string;
  dueDate: string;
  status: string;
  subtotal: string;
  taxTotal: string;
  total: string;
  totalPaid?: string;
  paymentCount?: number;
  customer: {
    nombre: string;
    apellidoPaterno: string;
    nui?: string;
    numeroIdentificacionUnico?: string;
    group?: { name: string };
  } | null;
  items: Array<{
    description?: string;
    quantity: string;
    unitPrice: string;
    product?: { name: string };
  }>;
}

const paymentMethodLabels: Record<string, string> = {
  TRANSFER: 'Transferencia Bancaria',
  CASH: 'Efectivo',
  CHECK: 'Cheque',
  CARD: 'Tarjeta',
  OTHER: 'Otro',
};

function formatDate(dateStr: string) {
  return formatLocalDate(dateStr, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function formatDateShort(dateStr: string) {
  return formatLocalDate(dateStr, {
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
}

function formatCurrency(amount: string | number) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(amount));
}

// ── Excel / CSV download ─────────────────────────────────────────────────────
function downloadExcel(payments: Payment[]) {
  const headers = ['ID', 'Folio', 'Fecha de Pago', 'Factura', 'NUI', 'Cliente', 'Método', 'Cuenta Destino', 'Usuario', 'Referencia', 'Notas', 'Monto'];
  const rows = payments.map(p => [
    p.id,
    p.folioSeriesId ? `RS${p.invoicingSettingsId}-${p.serieName}-${String(p.folio).padStart(3, '0')}` : '',
    formatDateShort(p.paymentDate),
    p.invoiceNumber,
    p.customerNui,
    p.customerName,
    paymentMethodLabels[p.paymentMethod] ?? p.paymentMethod,
    p.bankAccountName,
    p.createdByName || '',
    p.reference || '',
    p.notes || '',
    p.status === 'cancelled' ? '0.00' : Number(p.amount).toFixed(2),
  ]);

  // BOM + CSV so Excel opens it correctly in UTF-8
  const bom = '\uFEFF';
  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');

  const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pagos-recibidos-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Print receipt (same style as InvoicesList printInvoice) ─────────────────
function buildAndPrint(payment: Payment, inv: InvoiceDetail, companyName: string, logoUrl: string | null) {
  const printDate = new Date().toLocaleDateString('es-MX', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const customerName = `${inv.customer?.nombre || ''} ${inv.customer?.apellidoPaterno || ''}`.trim();
  const nui = inv.customer?.nui || inv.customer?.numeroIdentificacionUnico || '-';
  const subtotal = parseFloat(inv.subtotal || '0');
  const taxTotal = parseFloat(inv.taxTotal || '0');
  const invoiceTotal = parseFloat(inv.total || '0');
  const amountPaid = parseFloat(payment.amount);
  const fmt = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2 });

  const itemRows = (inv.items || []).map(item => {
    const name = item.description || item.product?.name || '-';
    const qty = parseFloat(item.quantity || '1');
    const price = parseFloat(item.unitPrice || '0');
    const lineTotal = qty * price;
    return `<tr>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${name}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${qty}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">$${fmt(price)}</td>
      <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">$${fmt(lineTotal)}</td>
    </tr>`;
  }).join('');

  // Pronto pago discount: only when:
  //   1. Invoice is PAID
  //   2. This is the sole payment on the invoice (paymentCount === 1) — confirms a write-off
  //      closed the remaining balance; multi-payment invoices never show this label
  //   3. amountPaid < invoiceTotal (the write-off bridged the gap)
  const promotionDiscount = parseFloat(payment.promotionDiscountAmount || '0');
  const hasPromotion = !!payment.promotionId && promotionDiscount > 0;

  const invoiceIsPaid = inv.status === 'PAID';
  const isSinglePayment = (inv.paymentCount ?? 1) === 1;
  const isProntoPago = invoiceIsPaid && isSinglePayment && amountPaid < invoiceTotal - 0.01;
  const isRecargo = invoiceIsPaid && isSinglePayment && amountPaid > invoiceTotal + 0.01;
  const discount = isProntoPago ? invoiceTotal - amountPaid : 0;
  const surcharge = isRecargo ? amountPaid - invoiceTotal : 0;

  // Guard against corrupted taxTotal in the DB (taxTotal should not exceed subtotal)
  const safeSubtotal = subtotal > 0 ? subtotal : invoiceTotal / 1.16;
  const safeTaxTotal = taxTotal > safeSubtotal ? Math.round(safeSubtotal * 0.16 * 100) / 100 : taxTotal;
  const safeInvoiceTotal = subtotal > 0 && taxTotal <= safeSubtotal
    ? invoiceTotal
    : Math.round(safeSubtotal * 1.16 * 100) / 100;

  let totalsRows = `
    <tr><td>Subtotal:</td><td style="text-align:right;">$${fmt(safeSubtotal)}</td></tr>
    <tr><td>IVA (16%):</td><td style="text-align:right;">$${fmt(safeTaxTotal)}</td></tr>`;

  if (hasPromotion) {
    totalsRows += `
    <tr class="promo-row"><td>🟣 Descuento Promoción${payment.promotionName ? ` (${payment.promotionName})` : ''}:</td><td style="text-align:right;">-$${fmt(promotionDiscount)}</td></tr>`;
  }

  if (isProntoPago) {
    totalsRows += `
    <tr class="invoice-total"><td>Total factura:</td><td style="text-align:right;">$${fmt(safeInvoiceTotal)}</td></tr>
    <tr class="discount-row"><td>🟢 Descuento Pronto Pago:</td><td style="text-align:right;">-$${fmt(discount)}</td></tr>
    <tr class="grand"><td>Total pagado:</td><td style="text-align:right;">$${fmt(amountPaid)}</td></tr>`;
  } else if (isRecargo) {
    totalsRows += `
    <tr class="invoice-total"><td>Total factura:</td><td style="text-align:right;">$${fmt(safeInvoiceTotal)}</td></tr>
    <tr class="surcharge-row"><td>🔴 Recargo por Mora:</td><td style="text-align:right;">+$${fmt(surcharge)}</td></tr>
    <tr class="grand"><td>Total pagado:</td><td style="text-align:right;">$${fmt(amountPaid)}</td></tr>`;
  } else {
    totalsRows += `
    <tr class="grand"><td>Total pagado:</td><td style="text-align:right;">$${fmt(amountPaid)}</td></tr>`;
  }

  const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8"/>
<title>Recibo de Pago ${payment.invoiceNumber}</title>
<style>
  * { box-sizing:border-box;margin:0;padding:0; }
  body { font-family:Arial,sans-serif;padding:28px;color:#111;font-size:14px; }
  .top-bar { display:flex;justify-content:space-between;align-items:center;margin-bottom:14px; }
  .company-block { display:flex;align-items:center;gap:12px; }
  .company-logo { max-height:60px;max-width:160px;object-fit:contain; }
  .company-name { font-size:18px;font-weight:bold;color:#111; }
  .print-date { font-size:12px;color:#6b7280;text-align:right; }
  .header { display:flex;justify-content:space-between;align-items:flex-start;border-top:2px solid #111;border-bottom:2px solid #111;padding:12px 0;margin-bottom:18px; }
  .title { font-size:20px;font-weight:bold;margin-bottom:4px; }
  .folio { color:#6b7280;font-size:13px;margin-bottom:6px; }
  .badge { display:inline-block;padding:2px 10px;border-radius:12px;font-size:12px;font-weight:bold;background:#dcfce7;color:#166534; }
  .meta { text-align:right;font-size:13px;color:#374151;line-height:2; }
  .meta strong { color:#111; }
  .info-grid { display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:18px; }
  .info-block { background:#f9fafb;padding:10px 12px;border-radius:6px; }
  .info-block .label { font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:.05em;margin-bottom:3px; }
  .info-block .value { font-weight:600;font-size:14px; }
  .info-block .sub { font-size:12px;color:#6b7280;margin-top:2px; }
  table { width:100%;border-collapse:collapse;font-size:13px;margin-bottom:16px; }
  thead th { background:#f3f4f6;padding:8px;font-size:11px;color:#374151;text-transform:uppercase; }
  thead th:not(:first-child) { text-align:right; }
  .totals-table { width:300px;margin-left:auto;border-collapse:collapse;font-size:13px; }
  .totals-table td { padding:4px 8px; }
  .totals-table .invoice-total td { color:#374151;border-top:1px solid #e5e7eb;padding-top:6px; }
  .totals-table .discount-row td { color:#166534;font-weight:600;background:#f0fdf4; }
  .totals-table .surcharge-row td { color:#991b1b;font-weight:600;background:#fef2f2; }
  .totals-table .promo-row td { color:#5b21b6;font-weight:600;background:#f5f3ff; }
  .totals-table .grand td { font-weight:bold;font-size:16px;border-top:2px solid #111;padding-top:8px; }
  @media print { body { padding:0; } }
</style></head>
<body>
  <div class="top-bar">
    <div class="company-block">
      ${logoUrl ? `<img src="${logoUrl}" alt="${companyName}" class="company-logo"/>` : ''}
      <div class="company-name">${companyName}</div>
    </div>
    <div class="print-date">Impreso el ${printDate}</div>
  </div>
  <div class="header">
    <div>
      <div class="title">Recibo de Pago</div>
      <div class="folio">${payment.invoiceNumber}</div>
      <span class="badge">Pagada</span>
    </div>
    <div class="meta">
      <div><strong>Fecha de pago:</strong> ${formatDate(payment.paymentDate)}</div>
      <div><strong>Cuenta destino:</strong> ${payment.bankAccountName}</div>
    </div>
  </div>
  <div class="info-grid">
    <div class="info-block">
      <div class="label">Cliente</div>
      <div class="value">${customerName}</div>
      <div class="sub">NUI: ${nui}</div>
      ${inv.customer?.group ? `<div class="sub">Grupo: ${inv.customer.group.name}</div>` : ''}
    </div>
    <div class="info-block">
      <div class="label">Método de pago</div>
      <div class="value">${paymentMethodLabels[payment.paymentMethod] ?? payment.paymentMethod}</div>
      ${payment.reference ? `<div class="sub">Ref: ${payment.reference}</div>` : ''}
      ${payment.notes ? `<div class="sub">Nota: ${payment.notes}</div>` : ''}
    </div>
  </div>
  <table>
    <thead><tr>
      <th style="text-align:left;">Descripción</th>
      <th>Cant.</th><th>Precio Unit.</th><th>Importe</th>
    </tr></thead>
    <tbody>${itemRows || '<tr><td colspan="4" style="padding:8px;text-align:center;color:#6b7280;">Sin partidas</td></tr>'}</tbody>
  </table>
  <table class="totals-table">${totalsRows}</table>
  <script>window.onload = () => { window.print(); }<\/script>
</body></html>`;

  const win = window.open('', '_blank', 'width=800,height=650');
  if (win) { win.document.write(html); win.document.close(); }
  else alert('Permite las ventanas emergentes para imprimir el recibo.');
}

// ── Component ────────────────────────────────────────────────────────────────
export default function PaymentsList() {
  const { cachedData } = useDataCache();
  const [searchParams, setSearchParams] = useSearchParams();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [printingId, setPrintingId] = useState<number | null>(null);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const { toast } = useToast();

  // Cancelar y recapturar folio
  const [cancelDialogPayment, setCancelDialogPayment] = useState<Payment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNewSerieId, setCancelNewSerieId] = useState<number | null>(null);

  // Cancelación completa (reversa contable, sin borrar)
  const [fullCancelDialogPayment, setFullCancelDialogPayment] = useState<Payment | null>(null);
  const [fullCancelReason, setFullCancelReason] = useState('');

  const empresaParam = searchParams.get('empresa');
  const [selectedEmpresaId, setSelectedEmpresaId] = useState<number | null>(
    empresaParam ? parseInt(empresaParam) : null
  );

  useEffect(() => {
    const p = searchParams.get('empresa');
    setSelectedEmpresaId(p ? parseInt(p) : null);
  }, [empresaParam]);

  const handleSetEmpresa = (id: number | null) => {
    setSelectedEmpresaId(id);
    setSerieFilter([]); // series pertenecen a una Razón Social específica — no son congruentes al cambiar de empresa
    const newParams = new URLSearchParams(searchParams);
    if (id) {
      newParams.set('empresa', String(id));
    } else {
      newParams.delete('empresa');
    }
    setSearchParams(newParams);
  };

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [serieFilter, setSerieFilter] = useState<number[]>([]);
  const [folioFromFilter, setFolioFromFilter] = useState('');
  const [folioToFilter, setFolioToFilter] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState<number | null>(null);

  // Pagination — this list can easily run into the thousands of rows; keeps the
  // DOM small and the table a fixed, short scroll instead of unbounded.
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPageSize, setPaymentsPageSize] = useState(50);

  const { data: allSeriesForFilter = [] } = useQuery<FolioSerieOption[]>({
    queryKey: ['payment-folio-series-all-for-filter'],
    queryFn: async () => {
      // Pulls every Razón Social's series (incluyendo inactivas — sus folios históricos siguen
      // existiendo) en un solo pase; el filtrado por empresa seleccionada se hace en el cliente.
      const res = await fetch('/api/payment-folio-series/all', { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
  });

  useEffect(() => {
    fetch('/api/finance/emisores', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setEmpresas(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const empresaLabel = (invoicingSettingsId: number) => {
    const emp = empresas.find(e => e.id === invoicingSettingsId);
    if (!emp) return `RS${invoicingSettingsId}`;
    return emp.profileName && emp.profileName !== 'Principal' ? emp.profileName : emp.legalName;
  };

  // Congruente con el filtro de "Empresa": si hay una Razón Social seleccionada, solo sus series
  // son elegibles; en "Todas las empresas" se muestran todas, diferenciadas por Razón Social.
  const visibleSeriesForFilter = useMemo(
    () => selectedEmpresaId
      ? allSeriesForFilter.filter(s => s.invoicingSettingsId === selectedEmpresaId)
      : allSeriesForFilter,
    [allSeriesForFilter, selectedEmpresaId]
  );

  const fetchPayments = async (empresaId?: number | null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const eid = empresaId !== undefined ? empresaId : selectedEmpresaId;
      if (eid) params.set('empresaId', String(eid));
      if (serieFilter.length > 0) params.set('serieIds', serieFilter.join(','));
      if (folioFromFilter) params.set('folioFrom', folioFromFilter);
      if (folioToFilter) params.set('folioTo', folioToFilter);
      if (createdByFilter) params.set('createdByUserId', String(createdByFilter));
      const response = await fetch(`/api/treasury/payments?${params}`, { credentials: 'include' });
      if (response.ok) setPayments(await response.json());
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPayments(); }, [selectedEmpresaId, createdByFilter]);

  // Cashiers to offer in the "Registrado por" filter. Accumulated (never shrunk) across
  // fetches so that once "Registrado por: Cajero B" narrows `payments` down to just their
  // rows, Cajero A doesn't vanish from the dropdown — the accumulator remembers everyone
  // seen this session instead of being re-derived from the currently filtered set.
  const [cashierOptions, setCashierOptions] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    setCashierOptions((prev) => {
      const seen = new Map(prev.map((c) => [c.id, c.name]));
      for (const p of payments) {
        if (p.createdByUserId && p.createdByName) seen.set(p.createdByUserId, p.createdByName);
      }
      return Array.from(seen.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    });
  }, [payments]);

  const { data: cancelSeriesOptions = [] } = useQuery({
    queryKey: ['payment-folio-series-for-cancel', cancelDialogPayment?.invoicingSettingsId],
    queryFn: async () => {
      const res = await fetch(`/api/payment-folio-series?invoicingSettingsId=${cancelDialogPayment!.invoicingSettingsId}`, { credentials: 'include' });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!cancelDialogPayment?.invoicingSettingsId,
  });

  const cancelAndRecaptureMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/treasury/payments/${cancelDialogPayment!.id}/cancel-and-recapture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: cancelReason, newSerieId: cancelNewSerieId }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error);
      }
      return res.json();
    },
    onSuccess: () => {
      fetchPayments();
      setCancelDialogPayment(null);
      setCancelReason('');
      setCancelNewSerieId(null);
      toast({ title: 'Folio cancelado y recapturado correctamente' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const fullCancelMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/treasury/payments/${fullCancelDialogPayment!.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason: fullCancelReason }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error);
      }
      return res.json();
    },
    onSuccess: () => {
      fetchPayments();
      setFullCancelDialogPayment(null);
      setFullCancelReason('');
      toast({ title: 'Pago cancelado — la factura vuelve a estar pendiente de pago' });
    },
    onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' }),
  });

  const applyDateFilter = () => fetchPayments();

  const clearDates = () => {
    setDateFrom('');
    setDateTo('');
    setTimeout(() => fetchPayments(selectedEmpresaId), 50);
  };

  const filteredPayments = payments.filter(payment => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      payment.invoiceNumber?.toLowerCase().includes(term) ||
      payment.customerName?.toLowerCase().includes(term) ||
      payment.customerNui?.toLowerCase().includes(term) ||
      payment.reference?.toLowerCase().includes(term);
    const matchesMethod = methodFilter === 'all' || payment.paymentMethod === methodFilter;
    return matchesSearch && matchesMethod;
  });

  const totalReceived = filteredPayments
    .filter((p) => p.status !== 'cancelled')
    .reduce((sum, p) => sum + parseFloat(p.amount), 0);

  const paymentsTotalPages = Math.max(1, Math.ceil(filteredPayments.length / paymentsPageSize));
  const paymentsPageClamped = Math.min(paymentsPage, paymentsTotalPages);
  const pagedFilteredPayments = filteredPayments.slice(
    (paymentsPageClamped - 1) * paymentsPageSize,
    paymentsPageClamped * paymentsPageSize
  );

  // Any filter change can shrink the result set below the page the user was on.
  useEffect(() => { setPaymentsPage(1); }, [searchTerm, methodFilter, payments]);

  const handlePrint = async (payment: Payment) => {
    setPrintingId(payment.id);
    try {
      const res = await fetch(`/api/sales/invoices/${payment.invoiceId}`, { credentials: 'include' });
      if (!res.ok) throw new Error('No se pudo cargar la factura');
      const inv: InvoiceDetail = await res.json();
      const companyName = (cachedData as any).company?.legalName || 'Institución Educativa';
      const logoUrl = (cachedData as any).company?.logoUrl
        ? `${window.location.origin}${(cachedData as any).company.logoUrl}`
        : null;
      buildAndPrint(payment, inv, companyName, logoUrl);
    } catch (err) {
      console.error('Error al imprimir recibo:', err);
      alert('No se pudo cargar la información de la factura para imprimir.');
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wallet className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Pagos Recibidos</h1>
            <p className="text-muted-foreground">Historial de pagos registrados</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadExcel(filteredPayments)}
            disabled={filteredPayments.length === 0}
            title="Descargar Excel"
          >
            <Download className="h-4 w-4 mr-2" />
            Descargar Excel
          </Button>
          <Button onClick={() => fetchPayments()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="space-y-3">
            {/* Empresa filter pills */}
            {empresas.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSetEmpresa(null)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                    selectedEmpresaId === null
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  Todas las empresas
                </button>
                {empresas.map(emp => (
                  <button
                    key={emp.id}
                    onClick={() => handleSetEmpresa(emp.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border flex items-center gap-1.5 ${
                      selectedEmpresaId === emp.id
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                    }`}
                  >
                    <Building2 className="w-3 h-3" />
                    {emp.profileName && emp.profileName !== 'Principal' ? emp.profileName : emp.legalName}
                  </button>
                ))}
              </div>
            )}

            {/* Row 1: Search + Method */}
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por NUI, nombre, factura o referencia..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={methodFilter} onValueChange={setMethodFilter}>
                <SelectTrigger className="w-[190px]">
                  <SelectValue placeholder="Método de pago" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los métodos</SelectItem>
                  <SelectItem value="TRANSFER">Transferencia</SelectItem>
                  <SelectItem value="CASH">Efectivo</SelectItem>
                  <SelectItem value="CHECK">Cheque</SelectItem>
                  <SelectItem value="CARD">Tarjeta</SelectItem>
                  <SelectItem value="OTHER">Otro</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={createdByFilter ? String(createdByFilter) : 'all'}
                onValueChange={(v) => setCreatedByFilter(v === 'all' ? null : Number(v))}
              >
                <SelectTrigger className="w-[190px]">
                  <SelectValue placeholder="Registrado por" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  {cashierOptions.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Serie</Label>
                <select
                  multiple
                  className="border rounded px-2 py-1 text-sm min-w-[100px]"
                  value={serieFilter.map(String)}
                  onChange={(e) => setSerieFilter(Array.from(e.target.selectedOptions).map((o) => Number(o.value)))}
                >
                  {visibleSeriesForFilter.map((s) => (
                    <option key={s.id} value={s.id}>
                      {selectedEmpresaId ? s.serie : `${empresaLabel(s.invoicingSettingsId)} — ${s.serie}`}
                      {!s.isActive ? ' (inactiva)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs">Folio</Label>
                <Input className="w-20" placeholder="De" value={folioFromFilter} onChange={(e) => setFolioFromFilter(e.target.value)} />
                <Input className="w-20" placeholder="A" value={folioToFilter} onChange={(e) => setFolioToFilter(e.target.value)} />
              </div>
            </div>

            {/* Row 2: Date range + total */}
            <div className="flex flex-col md:flex-row gap-3 items-end">
              <div className="flex gap-2 items-end flex-1">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Desde</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-[150px]"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Hasta</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-[150px]"
                  />
                </div>
                <Button size="sm" onClick={applyDateFilter} variant="secondary">
                  <Calendar className="h-4 w-4 mr-1" />
                  Filtrar
                </Button>
                {(dateFrom || dateTo) && (
                  <Button size="sm" variant="ghost" onClick={clearDates} title="Quitar filtro de fechas">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-lg shrink-0">
                <span className="text-sm text-muted-foreground">Total ({filteredPayments.length}):</span>
                <span className="text-lg font-bold text-green-600">{formatCurrency(totalReceived)}</span>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Wallet className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No se encontraron pagos</p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="text-center py-3 px-3 font-medium w-12">Recibo</th>
                    <th className="text-left py-3 px-3 font-medium">Folio</th>
                    <th className="text-left py-3 px-3 font-medium">Fecha</th>
                    <th className="text-left py-3 px-3 font-medium">Factura</th>
                    <th className="text-left py-3 px-3 font-medium">NUI</th>
                    <th className="text-left py-3 px-3 font-medium">Cliente</th>
                    <th className="text-left py-3 px-3 font-medium">Método</th>
                    <th className="text-left py-3 px-3 font-medium">Cuenta</th>
                    <th className="text-left py-3 px-3 font-medium">Usuario</th>
                    <th className="text-left py-3 px-3 font-medium">Referencia</th>
                    <th className="text-right py-3 px-3 font-medium">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFilteredPayments.map((payment) => (
                    <tr key={payment.id} className="border-b hover:bg-muted/30">
                      <td className="py-2.5 px-3 text-center">
                        <div className="flex items-center justify-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePrint(payment)}
                            disabled={printingId === payment.id}
                            title="Imprimir recibo"
                          >
                            <Printer className={`h-4 w-4 ${printingId === payment.id ? 'animate-pulse text-muted-foreground' : 'text-primary'}`} />
                          </Button>
                          {payment.folioSeriesId && payment.status === 'active' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setCancelDialogPayment(payment)}
                                title="Cancelar y recapturar en otra serie"
                              >
                                <XCircle className="h-4 w-4 text-red-500" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setFullCancelDialogPayment(payment)}
                                title="Cancelar pago (reversa completa, no se puede deshacer)"
                              >
                                <Ban className="h-4 w-4 text-red-700" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 font-mono text-xs">
                        {payment.folioSeriesId
                          ? `RS${payment.invoicingSettingsId}-${payment.serieName}-${String(payment.folio).padStart(3, '0')}`
                          : '—'}
                        {payment.status === 'cancelled' && (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700">
                            Cancelado
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          {formatDateShort(payment.paymentDate)}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 font-mono text-xs">{payment.invoiceNumber}</td>
                      <td className="py-2.5 px-3">
                        <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                          {payment.customerNui}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">{payment.customerName}</td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1.5">
                          <CreditCard className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-xs">{paymentMethodLabels[payment.paymentMethod] ?? payment.paymentMethod}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-xs">{payment.bankAccountName}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-xs text-muted-foreground">{payment.createdByName || '—'}</td>
                      <td className="py-2.5 px-3 text-muted-foreground text-xs">{payment.reference || '—'}</td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <span className={`font-semibold ${payment.status === 'cancelled' ? 'text-gray-400' : 'text-green-700'}`}>
                            {formatCurrency(payment.status === 'cancelled' ? 0 : payment.amount)}
                          </span>
                          {payment.promotionId && parseFloat(payment.promotionDiscountAmount || '0') > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 whitespace-nowrap cursor-default"
                              title={`${payment.promotionName ?? 'Promoción aplicada'} · Descuento: ${formatCurrency(payment.promotionDiscountAmount || '0')}`}
                            >
                              <Sparkles className="w-2.5 h-2.5" />
                              Promo -{formatCurrency(payment.promotionDiscountAmount || '0')}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between gap-3 border-t px-1 py-3 flex-wrap">
              <p className="text-xs text-muted-foreground">
                Mostrando {(paymentsPageClamped - 1) * paymentsPageSize + 1}
                –{Math.min(paymentsPageClamped * paymentsPageSize, filteredPayments.length)} de {filteredPayments.length}
              </p>
              <div className="flex items-center gap-2">
                <Select value={String(paymentsPageSize)} onValueChange={v => { setPaymentsPageSize(Number(v)); setPaymentsPage(1); }}>
                  <SelectTrigger className="w-[100px] h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[50, 100, 150].map(s => (
                      <SelectItem key={s} value={String(s)}>{s} / pág</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPaymentsPage(p => Math.max(1, p - 1))}
                  disabled={paymentsPageClamped === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-muted-foreground min-w-[90px] text-center">
                  Página {paymentsPageClamped} de {paymentsTotalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPaymentsPage(p => Math.min(paymentsTotalPages, p + 1))}
                  disabled={paymentsPageClamped === paymentsTotalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {cancelDialogPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4">
            <h2 className="text-lg font-semibold">Cancelar y recapturar folio</h2>
            <p className="text-sm text-gray-500">
              Esto cancela el folio actual (queda visible en el reporte, no se borra) y registra
              un pago nuevo idéntico en la serie correcta.
            </p>
            <div>
              <Label className="text-xs">Motivo *</Label>
              <Input
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ej. Alumno solicitó factura"
              />
            </div>
            <div>
              <Label className="text-xs">Serie correcta *</Label>
              <select
                className="w-full border rounded px-2 py-1.5 text-sm"
                value={cancelNewSerieId ?? ''}
                onChange={(e) => setCancelNewSerieId(Number(e.target.value))}
              >
                <option value="">Selecciona una serie</option>
                {cancelSeriesOptions
                  .filter((s: any) => s.isActive && s.id !== cancelDialogPayment.folioSeriesId)
                  .map((s: any) => (
                    <option key={s.id} value={s.id}>
                      {s.serie}{s.descripcion ? ` — ${s.descripcion}` : ''}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCancelDialogPayment(null)}>Cerrar</Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                disabled={!cancelReason.trim() || !cancelNewSerieId || cancelAndRecaptureMutation.isPending}
                onClick={() => cancelAndRecaptureMutation.mutate()}
              >
                {cancelAndRecaptureMutation.isPending ? 'Procesando...' : 'Cancelar y recapturar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {fullCancelDialogPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5 space-y-4">
            <h2 className="text-lg font-semibold text-red-700">Cancelar pago</h2>
            <p className="text-sm text-gray-500">
              Esto revierte el pago contablemente y lo marca como cancelado (no se borra, queda
              visible en el reporte). La factura/colegiatura asociada vuelve a quedar pendiente de
              pago para poder registrarse de nuevo correctamente. No aplica si el pago ya tiene un
              CFDI timbrado.
            </p>
            <div>
              <Label className="text-xs">Motivo *</Label>
              <Input
                value={fullCancelReason}
                onChange={(e) => setFullCancelReason(e.target.value)}
                placeholder="Ej. Monto capturado sin aplicar el descuento correcto"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFullCancelDialogPayment(null)}>Cerrar</Button>
              <Button
                className="bg-red-700 hover:bg-red-800"
                disabled={!fullCancelReason.trim() || fullCancelMutation.isPending}
                onClick={() => fullCancelMutation.mutate()}
              >
                {fullCancelMutation.isPending ? 'Procesando...' : 'Cancelar pago'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
